import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PersonalInfoSection from './PersonalInfoSection';
import SubscriptionStatus from './SubscriptionStatus';
import DashboardAccessSection from './DashboardAccessSection';
import SubscriptionPlansSection from './SubscriptionPlansSection';
import InstallPWAButton from './InstallPWAButton';
import { getSubscriptionDisplayName, mapPlanIdToSubscriptionType } from '../../utils/accountHelpers';
import { verifyPayment, syncSubscription } from '../../services/stripeService';
import { isRawStripeSubscription, sanitizeSubscription } from '../../utils/subscriptionHelpers';

const headerLogoSrc = '/images/misc/logoshape.svg';
const footerLogoSrc = '/images/logos/logo.svg';

export default function AccountPage() {
  const { t, i18n } = useTranslation();
  const { logout, user, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const accountText = t('account', { returnObjects: true, defaultValue: {} }) || {};
  const headerText = accountText.header || {};

  const languageOptions = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' }
  ];
  const activeLanguage = (i18n.language || 'en').startsWith('es') ? 'es' : 'en';
  const languageNames = { en: 'English', es: 'Español' };

  const handleLanguageChange = (code) => {
    if (code !== activeLanguage) {
      i18n.changeLanguage(code);
    }
  };

  const [userProfile, setUserProfile] = useState({ name: '', email: '', phone: '' });
  const [subscription, setSubscription] = useState({
    type: 'expired',
    status: 'expired',
    sessionsRemaining: null,
    expiresAt: null,
    cancelledAt: null
  });
  const [loading, setLoading] = useState(true);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState({ type: '', text: '' });

  // Logout handler
  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  const headerRef = useRef(null);
  const personalInfoRef = useRef(null);
  const dashboardsRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(70);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle payment verification from URL parameters and automatic subscription sync
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const portalReturned = searchParams.get('portal');

    const handlePaymentVerification = async () => {
      if (!user || !user.uid) {
        console.log('[AccountPage] No user, skipping payment verification');
        return;
      }

      // Handle return from Stripe Customer Portal
      if (portalReturned === 'returned') {
        // Prevent duplicate syncs
        const syncKey = 'portal_sync_in_progress';
        if (sessionStorage.getItem(syncKey)) {
          console.log('[AccountPage] Portal sync already in progress, skipping duplicate call');
          return;
        }

        console.log('[AccountPage] User returned from Customer Portal, syncing subscription...');
        sessionStorage.setItem(syncKey, 'true');
        setVerifyingPayment(true);
        setPaymentMessage({ type: 'info', text: 'Syncing subscription status...' });

        try {
          const result = await syncSubscription(user.uid);
          if (result.success) {
            setPaymentMessage({ type: 'success', text: 'Subscription updated successfully!' });
            // Reload user data to get updated subscription
            await reloadUser();
            // Remove portal parameter from URL
            searchParams.delete('portal');
            setSearchParams(searchParams, { replace: true });
          } else {
            setPaymentMessage({ type: 'error', text: result.error || 'Failed to sync subscription' });
          }
        } catch (error) {
          console.error('[AccountPage] Error syncing subscription:', error);
          setPaymentMessage({ type: 'error', text: 'Failed to sync subscription. Please try again.' });
        } finally {
          setVerifyingPayment(false);
          sessionStorage.removeItem(syncKey);
          // Clear message after 5 seconds
          setTimeout(() => setPaymentMessage({ type: '', text: '' }), 5000);
        }
        return;
      }

      // Handle payment verification from Stripe checkout
      if (sessionId) {
        console.log('[AccountPage] Payment session ID found in URL, verifying payment...', sessionId);
        setVerifyingPayment(true);
        setPaymentMessage({ type: 'info', text: 'Verifying payment...' });

        try {
          const result = await verifyPayment(sessionId, user.uid);
          if (result.success) {
            setPaymentMessage({ type: 'success', text: 'Payment verified! Subscription activated.' });
            // Reload user data to get updated subscription
            await reloadUser();
            // Remove session_id parameter from URL
            searchParams.delete('session_id');
            setSearchParams(searchParams, { replace: true });
          } else {
            console.error('[AccountPage] Payment verification failed:', result.error);
            setPaymentMessage({ type: 'error', text: result.error || 'Failed to verify payment. Please contact support.' });
          }
        } catch (error) {
          console.error('[AccountPage] Error verifying payment:', error);
          setPaymentMessage({ type: 'error', text: 'Error verifying payment. Please try again or contact support.' });
        } finally {
          setVerifyingPayment(false);
          // Clear message after 5 seconds
          setTimeout(() => setPaymentMessage({ type: '', text: '' }), 5000);
        }
      } else {
        // No session_id - Payment Links don't add it. User may have just completed payment.
        // Run sync if: (a) we set redirecting_to_stripe before leaving, or (b) referrer is Stripe, or (c) first visit
        const redirectingTs = sessionStorage.getItem('redirecting_to_stripe');
        const referrerIsStripe = typeof document !== 'undefined' && document.referrer &&
          (document.referrer.includes('stripe.com') || document.referrer.includes('buy.stripe.com'));
        const returningFromStripe = referrerIsStripe || (redirectingTs && (Date.now() - parseInt(redirectingTs, 10)) < 10 * 60 * 1000);
        const hasTriedFallback = sessionStorage.getItem('payment_verification_attempted');

        if ((returningFromStripe || !hasTriedFallback) && user && user.uid) {
          if (returningFromStripe) {
            sessionStorage.removeItem('redirecting_to_stripe');
          }
          sessionStorage.setItem('payment_verification_attempted', 'true');
          console.log('[AccountPage] Syncing subscription (returningFromStripe:', returningFromStripe, ', referrer:', document.referrer?.slice(0, 50) + '...');
          setVerifyingPayment(true);

          try {
            const result = await syncSubscription(user.uid);
            if (result.success) {
              setPaymentMessage({ type: 'success', text: 'Subscription found and synced successfully!' });
              await reloadUser();
              sessionStorage.removeItem('payment_verification_attempted');
            } else {
              console.log('[AccountPage] Subscription sync fallback result:', result.error || 'No active subscription found');
            }
          } catch (error) {
            console.error('[AccountPage] Subscription sync fallback error:', error);
            sessionStorage.removeItem('payment_verification_attempted');
          } finally {
            setVerifyingPayment(false);
          }
        }
      }
    };

    // Always run verification whenever we have a user.
    // The inner logic uses sessionStorage flags to avoid duplicate fallback syncs.
    if (user && user.uid) {
      handlePaymentVerification();
    }
  }, [user, searchParams, setSearchParams, reloadUser]);

  // Sync subscription when page becomes visible (user returns from Customer Portal)
  // Note: This is disabled to prevent conflicts with URL parameter-based sync
  // The portal return is handled by the searchParams useEffect above
  // Uncomment if you want automatic syncing on visibility change
  /*
  useEffect(() => {
    if (!user || !user.uid || !user.subscription?.stripeCustomerId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Skip if we just handled portal return (avoid duplicate syncs)
        if (searchParams.get('portal') === 'returned') {
          return;
        }
        
        // Check if we should sync (not synced in last 2 minutes)
        const lastSync = sessionStorage.getItem('subscription_sync_timestamp');
        const now = Date.now();
        const twoMinutes = 2 * 60 * 1000;

        if (!lastSync || (now - parseInt(lastSync)) > twoMinutes) {
          console.log('[AccountPage] Page visible, checking subscription status...');
          // Sync in background
          import('../../services/stripeService').then(({ syncSubscription }) => {
            syncSubscription(user.uid).then((result) => {
              if (result.success) {
                console.log('[AccountPage] Subscription synced after page visibility change');
                sessionStorage.setItem('subscription_sync_timestamp', Date.now().toString());
                reloadUser();
              }
            }).catch((error) => {
              console.error('[AccountPage] Sync error on visibility change:', error);
            });
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, reloadUser, searchParams]);
  */

  // Load user data from Firebase
  useEffect(() => {
    if (user) {
      // Set user profile from Firebase user data
      // Priority: 1. user.name (from Firestore), 2. firstName + lastName (from Firestore), 3. email (fallback)
      setUserProfile({
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        email: user.email || '',
        phone: user.phone || ''
      });

      // Set subscription from Firebase user data
      if (user.subscription) {
        // CRITICAL: Validate subscription data to prevent raw Stripe objects
        if (isRawStripeSubscription(user.subscription)) {
          console.error('❌ CRITICAL: Raw Stripe subscription object detected in AccountPage!');
          
          // Trigger sync to fix corrupted data
          if (user.uid) {
            syncSubscription(user.uid).then((result) => {
              if (result.success) {
                console.log('[AccountPage] Subscription synced successfully, reloading user data...');
                reloadUser();
              } else {
                console.error('[AccountPage] Failed to sync subscription:', result.error);
              }
            }).catch((error) => {
              console.error('[AccountPage] Error syncing subscription:', error);
            });
          }
          
          // Set to expired to prevent errors
          setSubscription({
            type: 'expired',
            status: 'expired',
            sessionsRemaining: null,
            expiresAt: null,
            cancelledAt: null
          });
        } else {
          // Sanitize subscription data
          const sub = sanitizeSubscription(user.subscription) || user.subscription;
          
          // Valid subscription data - proceed normally
          // Map Firestore planId to component subscription type
          const subscriptionType = mapPlanIdToSubscriptionType(sub.planId);
          
          // Normalize status: Firestore uses "canceled", component uses "cancelled"
          let normalizedStatus = sub.status || 'expired';
          if (normalizedStatus === 'canceled') {
            normalizedStatus = 'cancelled';
          }
          
          console.log('[AccountPage] Loading subscription:', {
            planId: sub.planId,
            mappedType: subscriptionType,
            status: sub.status,
            normalizedStatus,
            stripeCustomerId: sub.stripeCustomerId,
            stripeSubscriptionId: sub.stripeSubscriptionId
          });
          
          setSubscription({
            type: subscriptionType,
            status: normalizedStatus,
            sessionsRemaining: sub.sessionsRemaining ?? null,
            expiresAt: sub.currentPeriodEnd || null,
            cancelledAt: sub.cancelAtPeriodEnd && sub.currentPeriodEnd 
              ? sub.currentPeriodEnd 
              : null
          });
        }

        // Auto-sync subscription if user has Stripe customer ID
        // This ensures subscription status is up-to-date, especially after changes in Customer Portal
        if (user.subscription.stripeCustomerId && !verifyingPayment && user.uid) {
          // Only sync once per 2 minutes to avoid excessive API calls
          const lastSync = sessionStorage.getItem('subscription_sync_timestamp');
          const now = Date.now();
          const twoMinutes = 2 * 60 * 1000;
          
          if (!lastSync || (now - parseInt(lastSync)) > twoMinutes) {
            console.log('[AccountPage] Auto-syncing subscription from Stripe...');
            sessionStorage.setItem('subscription_sync_timestamp', now.toString());
            
            // Sync in background
            syncSubscription(user.uid).then((result) => {
              if (result.success) {
                console.log('[AccountPage] Subscription auto-synced successfully');
                reloadUser();
              } else {
                console.log('[AccountPage] Auto-sync result:', result.error);
              }
            }).catch((error) => {
              console.error('[AccountPage] Auto-sync error:', error);
            });
          }
        }
      } else {
        // Explicitly handle case when subscription doesn't exist
        // This should only happen for brand new users who haven't subscribed yet
        console.log('[AccountPage] No subscription data found for user, setting to expired');
        setSubscription({
          type: 'expired',
          status: 'expired',
          sessionsRemaining: null,
          expiresAt: null,
          cancelledAt: null
        });
      }

      setLoading(false);
    } else {
      // If no user, redirect to sign in (ProtectedRoute should handle this, but just in case)
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProfileUpdate = (updatedProfile) => setUserProfile(updatedProfile);
  
  const handleSubscriptionUpdate = async (updatedSubscription) => {
    // Update local state
    setSubscription(updatedSubscription);
    
    // Reload user data from Firestore to ensure we have the latest subscription info
    // This ensures that if subscription was updated elsewhere (e.g., via webhook),
    // we get the latest data
    try {
      await reloadUser();
    } catch (error) {
      console.error('Error reloading user data after subscription update:', error);
    }
  };

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    const elementTop = sectionRef.current.getBoundingClientRect().top + window.scrollY;
    const offset = (headerHeight || 110) + 20;
    window.scrollTo({
      top: Math.max(elementTop - offset, 0),
      behavior: 'smooth'
    });
  };

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && headerRef.current) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .account-page {
        font-family: "Montserrat", "Roboto", sans-serif;
        background-color: #161b23;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      .fixed-top-module {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 70px;
        background: rgba(22, 27, 35, 0.05);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 48px;
        transition: background 0.3s ease, box-shadow 0.3s ease;
      }

      .fixed-top-module.scrolled {
        background: rgba(22, 27, 35, 0.2);
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
      }

      .account-content-wrapper {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .account-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 16px;
        flex-wrap: nowrap;
        min-width: 0;
      }

      .top-bar-logo {
        height: 40px;
        width: auto;
        display: flex;
        align-items: center;
        gap: 16px;
        transition: opacity 0.3s ease;
      }

      .top-bar-logo img {
        height: 20px;
        width: auto;
        opacity: 1;
        transition: opacity 0.3s ease;
      }

      .top-bar-logo:hover img {
        opacity: 1;
      }

      .brand-text {
        display: flex;
        align-items: center;
      }

      .brand-pill {
        font-size: 0.7rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: rgba(150, 164, 189, 0.7);
        font-weight: 500;
        white-space: nowrap;
      }

      .top-bar-buttons {
        display: flex;
        gap: 16px;
        align-items: center;
        flex-wrap: nowrap;
        min-width: 0;
      }

      .top-bar-button {
        padding: 8px 16px;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        text-decoration: none;
        border-radius: 8px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        white-space: nowrap;
        min-width: 0;
        flex-shrink: 1;
        border: none;
        cursor: pointer;
        font-family: "Montserrat", "Roboto", sans-serif;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .top-bar-button.primary {
        background: #49a9ff;
        color: #f4f7fb;
        box-shadow: 0 2px 8px rgba(73, 169, 255, 0.3);
      }

      .top-bar-button.primary:hover {
        background: #63c6ff;
        box-shadow: 0 4px 12px rgba(73, 169, 255, 0.4);
        transform: translateY(-1px);
      }

      .top-bar-button.secondary {
        background: transparent;
        color: #49a9ff;
        border: 1.5px solid #49a9ff;
      }

      .top-bar-button.secondary:hover {
        background: #49a9ff;
        color: #f4f7fb;
        transform: translateY(-1px);
      }

      .top-bar-button.tertiary {
        background: transparent;
        color: rgba(255, 255, 255, 0.7);
        border: none;
        padding: 10px 16px;
      }

      .top-bar-button.tertiary:hover {
        color: #49a9ff;
        background: rgba(255, 255, 255, 0.05);
        transform: translateY(-1px);
      }

      .top-bar-button.active {
        color: #49a9ff;
        background: rgba(73, 169, 255, 0.1);
      }

      .language-toggle {
        background: transparent;
        border: 1px solid rgba(73, 169, 255, 0.3);
        border-radius: 6px;
        padding: 6px 12px;
        color: #f4f7fb;
        font-family: "Montserrat", sans-serif;
        font-size: 0.85rem;
        font-weight: 500;
        letter-spacing: 0.05em;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 6px;
        margin-right: 0;
      }

      .language-toggle:hover {
        border-color: rgba(73, 169, 255, 0.6);
        background: rgba(73, 169, 255, 0.1);
      }

      .language-toggle:focus {
        outline: 2px solid rgba(73, 169, 255, 0.5);
        outline-offset: 2px;
      }

      .lang-option {
        transition: color 0.3s ease;
        user-select: none;
      }

      .lang-option.active {
        color: #49a9ff;
        font-weight: 600;
      }

      .lang-option:hover {
        color: #63c6ff;
      }

      .lang-separator {
        color: rgba(150, 164, 189, 0.6);
        opacity: 0.5;
        user-select: none;
      }

      .account-dropdown {
        position: relative;
      }

      .account-dropdown-menu {
        position: absolute;
        right: 0;
        top: 100%;
        margin-top: 8px;
        width: 180px;
        background: rgba(22, 27, 35, 0.95);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 8px;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-8px);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
        z-index: 50;
      }

      .account-dropdown:hover .account-dropdown-menu {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }

      .account-dropdown-item {
        display: block;
        width: 100%;
        padding: 10px 14px;
        color: rgba(255, 255, 255, 0.9);
        text-decoration: none;
        font-size: 0.8rem;
        font-weight: 500;
        letter-spacing: 0.05em;
        border-radius: 6px;
        transition: all 0.2s ease;
        text-align: left;
        border: none;
        background: transparent;
        cursor: pointer;
        font-family: "Montserrat", "Roboto", sans-serif;
      }

      .account-dropdown-item:hover {
        background: rgba(73, 169, 255, 0.15);
        color: #49a9ff;
      }

      .scrollable-account-body {
        padding-bottom: 60px;
        overflow-y: auto;
        flex-grow: 1;
        width: 100%;
        padding-top: 70px;
      }

      .scrollable-inner-container {
        max-width: 980px;
        margin: 0 auto;
        padding: 0 1.25rem;
      }

      .account-module {
        background-color: #2a3138;
        border-radius: 10px;
        padding: 26px;
        margin-bottom: 20px;
        color: #f0f0f0;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
      }

      .account-module:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px rgba(0, 0, 0, 0.45);
      }

      .custom-hr {
        border: none;
        height: 1px;
        background-color: #3b3b3b;
        margin: 1.25rem 0 1.5rem 0;
      }

      .footer-bar {
        background-color: #1f1f1f !important;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        height: 40px;
        z-index: 50;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      
      .account-page .footer-bar {
        background-color: #1f1f1f !important;
      }

      @media (max-width: 720px) {
        .fixed-top-module {
          padding: 0 20px;
          height: 60px;
        }

        .scrollable-account-body {
          padding-top: 60px;
        }

        .top-bar-logo {
          height: 32px;
          gap: 12px;
        }

        .top-bar-logo img {
          height: 16px;
        }

        .top-bar-buttons {
          gap: 8px;
        }

        .top-bar-button {
          padding: 8px 14px;
          font-size: 0.75rem;
        }

        .top-bar-button.tertiary {
          padding: 8px 12px;
          font-size: 0.7rem;
        }

        .language-toggle {
          padding: 4px 8px;
          font-size: 0.7rem;
          gap: 4px;
        }

        .brand-pill {
          font-size: 0.65rem;
        }
      }

      @media (max-width: 620px) {
        .fixed-top-module {
          padding: 0 12px;
          height: 56px;
        }

        .scrollable-account-body {
          padding-top: 56px;
        }

        .top-bar-logo {
          height: 28px;
          gap: 8px;
          flex-shrink: 0;
        }

        .top-bar-logo img {
          height: 14px;
        }

        .top-bar-buttons {
          gap: 6px;
          flex-shrink: 1;
          min-width: 0;
        }

        .top-bar-button {
          padding: 6px 10px;
          font-size: 0.68rem;
          letter-spacing: 0.05em;
          max-width: 120px;
        }

        .top-bar-button.tertiary {
          padding: 6px 8px;
          font-size: 0.65rem;
          max-width: 100px;
        }

        .top-bar-button.secondary {
          padding: 6px 10px;
          font-size: 0.68rem;
          max-width: 110px;
        }

        .language-toggle {
          padding: 3px 6px;
          font-size: 0.65rem;
          gap: 3px;
          flex-shrink: 0;
        }

        .brand-pill {
          display: none;
        }
      }

      @media (max-width: 480px) {
        .fixed-top-module {
          padding: 0 12px;
          height: 54px;
        }

        .scrollable-account-body {
          padding-top: 54px;
        }

        .top-bar-logo {
          height: 26px;
          gap: 6px;
        }

        .top-bar-logo img {
          height: 13px;
        }

        .top-bar-buttons {
          gap: 4px;
          flex-wrap: wrap;
          max-width: calc(100% - 120px);
        }

        .top-bar-button {
          padding: 5px 8px;
          font-size: 0.65rem;
          letter-spacing: 0.04em;
        }

        .top-bar-button.tertiary {
          padding: 5px 7px;
          font-size: 0.62rem;
        }

        .top-bar-button.secondary {
          padding: 5px 8px;
          font-size: 0.65rem;
          max-width: 100px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .language-toggle {
          padding: 3px 5px;
          font-size: 0.62rem;
          gap: 3px;
        }

        .lang-option {
          font-size: 0.62rem;
        }

        .brand-pill {
          display: none;
        }
      }

      @media (max-width: 375px) {
        .fixed-top-module {
          padding: 0 8px;
          height: 52px;
        }

        .scrollable-account-body {
          padding-top: 52px;
        }

        .account-content-wrapper {
          overflow: hidden;
        }

        .account-bar {
          gap: 4px;
          min-width: 0;
        }

        .top-bar-logo {
          height: 24px;
          gap: 4px;
          flex-shrink: 0;
          min-width: 0;
        }

        .top-bar-logo img {
          height: 12px;
        }

        .top-bar-buttons {
          gap: 3px;
          flex-shrink: 1;
          min-width: 0;
          justify-content: flex-end;
        }

        .top-bar-button {
          padding: 4px 6px;
          font-size: 0.6rem;
          letter-spacing: 0.03em;
        }

        .top-bar-button.tertiary {
          padding: 4px 5px;
          font-size: 0.58rem;
        }

        .top-bar-button.secondary {
          padding: 4px 6px;
          font-size: 0.6rem;
          max-width: 80px;
        }

        .language-toggle {
          padding: 2px 4px;
          font-size: 0.6rem;
          gap: 2px;
        }

        .lang-option {
          font-size: 0.6rem;
        }

        .brand-pill {
          display: none;
        }
      }

      @media (max-width: 320px) {
        .fixed-top-module {
          padding: 0 6px;
          height: 50px;
        }

        .scrollable-account-body {
          padding-top: 50px;
        }

        .account-content-wrapper {
          overflow: hidden;
        }

        .account-bar {
          gap: 3px;
          min-width: 0;
        }

        .top-bar-logo {
          height: 22px;
          gap: 3px;
          flex-shrink: 0;
          min-width: 0;
        }

        .top-bar-logo img {
          height: 11px;
        }

        .top-bar-buttons {
          gap: 2px;
          flex-shrink: 1;
          min-width: 0;
          justify-content: flex-end;
        }

        .top-bar-button {
          padding: 3px 5px;
          font-size: 0.55rem;
          letter-spacing: 0.02em;
        }

        .top-bar-button.tertiary {
          padding: 3px 4px;
          font-size: 0.53rem;
        }

        .top-bar-button.secondary {
          padding: 3px 5px;
          font-size: 0.55rem;
          max-width: 70px;
        }

        .language-toggle {
          padding: 2px 3px;
          font-size: 0.55rem;
          gap: 2px;
        }

        .lang-option {
          font-size: 0.55rem;
        }

        .account-dropdown-menu {
          width: 150px;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) {
    return (
      <div className="account-page flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  const LOGO_FILTERS = {
    white: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(88deg) brightness(102%) contrast(101%)',
    blue: 'brightness(0) saturate(100%) invert(30%) sepia(96%) saturate(1916%) hue-rotate(197deg) brightness(102%) contrast(101%)',
    purple: 'brightness(0) saturate(100%) invert(37%) sepia(83%) saturate(1690%) hue-rotate(256deg) brightness(97%) contrast(101%)',
    yellow: 'brightness(0) saturate(100%) invert(86%) sepia(21%) saturate(1452%) hue-rotate(341deg) brightness(101%) contrast(102%)'
  };

  const getLogoFilterStyle = () => {
    const type = (subscription?.type || '').toLowerCase();
    const status = (subscription?.status || '').toLowerCase();

    if (!type || status === 'cancelled' || status === 'expired' || type === 'free_trial') {
      return { filter: LOGO_FILTERS.white };
    }

    switch (type) {
      case 'daily':
        return { filter: LOGO_FILTERS.blue };
      case 'monthly_global':
        return { filter: LOGO_FILTERS.purple };
      case 'monthly_all':
        return { filter: LOGO_FILTERS.yellow };
      default:
        return { filter: LOGO_FILTERS.white };
    }
  };

  const getSubscriptionPlanText = () => {
    const type = subscription?.type || 'expired';
    const status = subscription?.status || 'expired';
    
    // Handle cancelled/expired states
    if (status === 'cancelled' || status === 'expired' || type === 'expired') {
      return i18n.language === 'es' ? 'Sin Suscripción' : 'No Active Subscription';
    }

    // Get the subscription display name and translate it
    const displayName = getSubscriptionDisplayName(type);
    
    if (i18n.language === 'es') {
      const spanishNames = {
        'Free Trial': 'Prueba Gratuita',
        'Daily Subscription': 'Suscripción Diaria',
        'Monthly Global Markets': 'Mensual Mercados Globales',
        'Monthly All Dashboards': 'Mensual Todos los Dashboards',
        'No Active Subscription': 'Sin Suscripción',
        'Cancelled Subscription': 'Suscripción Cancelada'
      };
      return spanishNames[displayName] || displayName;
    }
    
    return displayName;
  };

  return (
    <div className="account-page">
      <div className={`fixed-top-module ${isScrolled ? 'scrolled' : ''}`} ref={headerRef}>
        <div className="account-content-wrapper">
          <div className="account-bar">
            <div className="top-bar-logo">
              <button 
                className="language-toggle" 
                onClick={() => handleLanguageChange(activeLanguage === 'en' ? 'es' : 'en')}
                aria-label={activeLanguage === 'en' ? 'Switch to Español' : 'Switch to English'}
              >
                <span className={`lang-option ${activeLanguage === 'en' ? 'active' : ''}`}>EN</span>
                <span className="lang-separator">|</span>
                <span className={`lang-option ${activeLanguage === 'es' ? 'active' : ''}`}>ES</span>
              </button>
              <Link to="/dashboard" className="flex items-center">
                <img
                  src={headerLogoSrc}
                  alt="Altradenative"
                  className="h-5 w-auto"
                  style={getLogoFilterStyle()}
                />
              </Link>
              <div className="brand-text">
                <span className="brand-pill">
                  {getSubscriptionPlanText()}
                </span>
              </div>
            </div>

            <div className="top-bar-buttons">
              <button
                type="button"
                className="top-bar-button tertiary active"
                onClick={() => scrollToSection(personalInfoRef)}
              >
                {headerText.account || 'Account'}
              </button>
              <button
                type="button"
                className="top-bar-button tertiary"
                onClick={() => scrollToSection(dashboardsRef)}
              >
                Dashboards
              </button>
              <div className="account-dropdown">
                <button className="top-bar-button secondary">
                  {userProfile.name || userProfile.email}
                </button>
                <div className="account-dropdown-menu">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogout();
                    }}
                    className="account-dropdown-item"
                  >
                    {headerText.logout || 'Logout'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="scrollable-account-body"
        style={{ paddingTop: `${(headerHeight || 110) + 24}px` }}
      >
        <div className="scrollable-inner-container">
          {/* Payment Verification Message */}
          {paymentMessage.text && (
            <div className={`account-module mb-4 ${
              paymentMessage.type === 'success' 
                ? 'bg-green-900/50 border-green-500' 
                : paymentMessage.type === 'error'
                ? 'bg-red-900/50 border-red-500'
                : 'bg-blue-900/50 border-blue-500'
            }`} style={{
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid',
              color: '#f0f0f0',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>{paymentMessage.text}</p>
                <button
                  onClick={() => setPaymentMessage({ type: '', text: '' })}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#f0f0f0',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    padding: '0 8px',
                    marginLeft: '12px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="account-module" ref={dashboardsRef}>
            <DashboardAccessSection
              subscription={user?.subscription || subscription}
            />
          </div>

          <div className="account-module" ref={personalInfoRef}>
            <PersonalInfoSection userProfile={userProfile} onUpdate={handleProfileUpdate} />
          </div>

          <div className="account-module">
            <SubscriptionStatus />
          </div>

          <div id="subscription-plans" className="account-module subscription-plans-module">
            <SubscriptionPlansSection />
          </div>

          <div className="h-12" />
        </div>
      </div>

      <footer className="footer-bar">
        <div className="flex items-center text-sm font-bold text-white">
          <img src={footerLogoSrc} alt="Altradenative" className="h-6" />
        </div>
      </footer>

      {/* PWA Install Button */}
      <InstallPWAButton />
    </div>
  );
}

